import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeftRight,
  Check,
  X,
  RefreshCw,
  Search,
  Lightbulb,
  FileSearch,
  Tag,
  History,
  Undo2,
  Inbox,
  ArrowUpDown,
  Sparkles,
  Workflow,
} from "lucide-react";
import { apiFetch, API } from "@/utils/api";
import ClassifySuggestion, { confidenceLabel } from "@/components/ClassifySuggestion";

// ── Helpers ──────────────────────────────────────────────────────────

function formatCurrency(amount, currency = "EUR") {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: currency || "EUR",
  }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-ES");
}

// ── Sub-components ───────────────────────────────────────────────────

function ConfidenceBadge({ confidence }) {
  const percent = Math.round(confidence * 100);
  let color = "bg-amber-100 text-amber-700";
  if (percent >= 90) color = "bg-emerald-100 text-emerald-700";
  else if (percent >= 70) color = "bg-blue-100 text-blue-700";

  return (
    <Badge className={color} variant="secondary">
      {percent}%
    </Badge>
  );
}

function AmountCell({ amount }) {
  const isNeg = amount < 0;
  return (
    <span
      className={`font-mono font-semibold ${
        isNeg ? "text-rose-600" : "text-emerald-600"
      }`}
    >
      {formatCurrency(amount)}
    </span>
  );
}

// ── Suggestions panel ────────────────────────────────────────────────

function SuggestionsPanel({ transaction, onConfirm, onClose }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const response = await apiFetch(
          `${API}/reconciliation/suggestions`
        );
        if (response.ok) {
          const data = await response.json();
          // Filter suggestions for this transaction
          const filtered = (data.suggestions || []).filter(
            (s) => s.transaction_id === transaction.transaction_id
          );
          setSuggestions(filtered);
        }
      } catch (error) {
        toast.error("Error al cargar sugerencias");
      } finally {
        setLoading(false);
      }
    };
    fetchSuggestions();
  }, [transaction.transaction_id]);

  const handleConfirm = async (suggestion) => {
    setConfirming(suggestion.suggestion_id);
    try {
      const params = new URLSearchParams({
        transaction_id: suggestion.transaction_id,
        invoice_id: suggestion.invoice_id,
        invoice_type: suggestion.invoice_type,
      });
      const response = await apiFetch(
        `${API}/reconciliation/confirm?${params}`,
        { method: "POST" }
      );
      if (response.ok) {
        toast.success("Conciliacion confirmada y asiento generado");
        onConfirm();
      } else if (response.status === 409) {
        const err = await response.json().catch(() => ({}));
        toast.warning(err.detail || "Movimiento ya conciliado.");
        onConfirm();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Error al conciliar");
      }
    } catch {
      toast.error("Error de red al conciliar. Refresca para ver el estado.");
    } finally {
      setConfirming(null);
    }
  };

  const handleReject = (suggestion) => {
    setSuggestions((prev) =>
      prev.filter((s) => s.suggestion_id !== suggestion.suggestion_id)
    );
    toast.info("Sugerencia descartada");
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-slate-500">
        <Lightbulb className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        No hay sugerencias para este movimiento
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-700">
          {suggestions.length} sugerencia{suggestions.length !== 1 && "s"}
        </span>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      {suggestions.map((s) => {
        const invoice = s.invoice;
        const name =
          s.invoice_type === "received"
            ? invoice.supplier_name
            : invoice.client_name;
        return (
          <div
            key={s.suggestion_id}
            className="border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-4"
            data-testid={`suggestion-${s.suggestion_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-slate-900 truncate">
                  {name}
                </span>
                <Badge variant="outline" className="text-xs shrink-0">
                  {s.invoice_type === "received"
                    ? "Recibida"
                    : "Emitida"}
                </Badge>
                <ConfidenceBadge confidence={s.confidence} />
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span>#{invoice.invoice_number}</span>
                <span>{formatDate(invoice.invoice_date)}</span>
                <span className="font-mono">
                  {formatCurrency(invoice.total_amount, invoice.currency)}
                </span>
              </div>
              {s.match_reasons && s.match_reasons.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">
                  {s.match_reasons.join(" - ")}
                </p>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleReject(s)}
                className="text-slate-600"
                data-testid={`reject-${s.suggestion_id}`}
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => handleConfirm(s)}
                disabled={confirming === s.suggestion_id}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid={`confirm-${s.suggestion_id}`}
              >
                {confirming === s.suggestion_id ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Invoice search panel ─────────────────────────────────────────────

function InvoiceSearchPanel({ transaction, onConfirm, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(null);
  const [pendingPartial, setPendingPartial] = useState(null);

  const searchInvoices = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ search: query });
      const [receivedRes, emittedRes] = await Promise.all([
        apiFetch(`${API}/invoices/received?${params}`),
        apiFetch(`${API}/invoices/emitted?${params}`),
      ]);

      const merged = [];
      if (receivedRes.ok) {
        const data = await receivedRes.json();
        (data.invoices || []).forEach((inv) =>
          merged.push({ ...inv, _type: "received" })
        );
      }
      if (emittedRes.ok) {
        const data = await emittedRes.json();
        (data.invoices || []).forEach((inv) =>
          merged.push({ ...inv, _type: "emitted" })
        );
      }
      setResults(merged);
    } catch {
      toast.error("Error al buscar facturas");
    } finally {
      setLoading(false);
    }
  };

  const doLink = async (invoice) => {
    setLinking(invoice.invoice_id);
    try {
      const params = new URLSearchParams({
        transaction_id: transaction.transaction_id,
        invoice_id: invoice.invoice_id,
        invoice_type: invoice._type,
      });
      const response = await apiFetch(
        `${API}/reconciliation/confirm?${params}`,
        { method: "POST" }
      );
      if (response.ok) {
        toast.success("Factura vinculada y asiento generado");
        onConfirm();
      } else if (response.status === 409) {
        const err = await response.json().catch(() => ({}));
        toast.warning(err.detail || "Movimiento ya conciliado.");
        onConfirm();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Error al vincular");
      }
    } catch {
      toast.error("Error de red al vincular. Refresca para ver el estado.");
    } finally {
      setLinking(null);
    }
  };

  // Decide si la vinculación es un pago/cobro parcial y, en ese caso, pide
  // confirmación explícita antes de registrarlo. Solo aplica a facturas EUR:
  // las de divisa extranjera = una conciliación es el pago completo (decisión
  // 2026-04-22 / 2026-06-05), nunca quedan parciales.
  const handleLink = (invoice) => {
    const paidSoFar =
      invoice._type === "received"
        ? invoice.amount_paid ?? 0
        : invoice.amount_collected ?? 0;
    const remaining = invoice.total_amount - paidSoFar;
    const txAmount = Math.abs(transaction.amount);
    const isEur = (invoice.currency || "EUR") === "EUR";

    if (isEur && txAmount < remaining - 0.01) {
      setPendingPartial({
        invoice,
        remaining,
        txAmount,
        willRemain: remaining - txAmount,
      });
      return;
    }
    doLink(invoice);
  };

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-700">
          Buscar factura
        </span>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Nombre, numero de factura..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchInvoices()}
            className="pl-10"
            data-testid="invoice-search-input"
          />
        </div>
        <Button
          variant="outline"
          onClick={searchInvoices}
          disabled={loading}
          data-testid="invoice-search-btn"
        >
          {loading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
        </Button>
      </div>

      {results.length === 0 && !loading && query && (
        <p className="text-sm text-slate-500 text-center py-3">
          Sin resultados
        </p>
      )}

      {results.length > 0 && (
        <div className="max-h-60 overflow-y-auto space-y-2">
          {results.map((inv) => {
            const name =
              inv._type === "received"
                ? inv.supplier_name
                : inv.client_name;
            // A2: un borrador no tiene asiento de venta — no se puede conciliar.
            const isDraft = inv._type === "emitted" && inv.status === "draft";
            return (
              <div
                key={`${inv._type}-${inv.invoice_id}`}
                className="border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-slate-900 truncate">
                      {name}
                    </span>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {inv._type === "received" ? "Recibida" : "Emitida"}
                    </Badge>
                    {isDraft && (
                      <Badge
                        variant="secondary"
                        className="text-xs shrink-0 bg-amber-100 text-amber-700"
                        data-testid={`draft-badge-${inv.invoice_id}`}
                      >
                        Borrador
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>#{inv.invoice_number}</span>
                    <span>{formatDate(inv.invoice_date)}</span>
                    <span className="font-mono">
                      {formatCurrency(inv.total_amount, inv.currency)}
                    </span>
                  </div>
                  {isDraft && (
                    <p className="text-xs text-amber-700 mt-1">
                      En borrador: usa "Emitir factura" en Facturas emitidas
                      antes de conciliar su cobro.
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => handleLink(inv)}
                  disabled={linking === inv.invoice_id || isDraft}
                  className="bg-blue-600 hover:bg-blue-700 shrink-0"
                  data-testid={`link-invoice-${inv.invoice_id}`}
                >
                  {linking === inv.invoice_id ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <ArrowLeftRight className="w-4 h-4 mr-1" />
                  )}
                  Vincular
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog
        open={pendingPartial !== null}
        onOpenChange={(o) => !o && setPendingPartial(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pago parcial</AlertDialogTitle>
            <AlertDialogDescription>
              El movimiento no cubre el total pendiente de la factura. Se
              registrará como pago parcial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingPartial && (
            <div className="space-y-2 text-sm rounded-lg border border-slate-200 p-3">
              <div className="flex justify-between">
                <span className="text-slate-500">Movimiento</span>
                <span className="font-mono font-semibold">
                  {formatCurrency(pendingPartial.txAmount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Pendiente de la factura</span>
                <span className="font-mono font-semibold">
                  {formatCurrency(pendingPartial.remaining)}
                </span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-2">
                <span className="text-slate-500">Quedarán pendientes</span>
                <span className="font-mono font-semibold text-amber-600">
                  {formatCurrency(pendingPartial.willRemain)}
                </span>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const inv = pendingPartial.invoice;
                setPendingPartial(null);
                doLink(inv);
              }}
            >
              Registrar pago parcial
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Classification panel ─────────────────────────────────────────────

function ClassifyPanel({ transaction, onConfirm, onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  // A4: categorías en lenguaje llano (mapean a cuentas PGC por debajo)
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showFullChart, setShowFullChart] = useState(false);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsRes, categoriesRes] = await Promise.all([
          apiFetch(`${API}/accounting/chart?order=usage`),
          apiFetch(`${API}/reconciliation/classify-categories`),
        ]);
        if (accountsRes.ok) {
          const data = await accountsRes.json();
          setAccounts(data.accounts || []);
        }
        if (categoriesRes.ok) {
          const data = await categoriesRes.json();
          setCategories(data.categories || []);
        }
      } catch {
        toast.error("Error al cargar cuentas");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredAccounts = accounts.filter((acc) => {
    if (!accountSearch) return true;
    const q = accountSearch.toLowerCase();
    return (
      acc.code.includes(q) || acc.name.toLowerCase().includes(q)
    );
  });

  // Gastos para cargos, ingresos para abonos
  const visibleCategories = categories.filter((c) =>
    transaction.amount < 0 ? c.kind === "gasto" : c.kind === "ingreso"
  );

  // 3.4: preselección desde la sugerencia (regla manual o IA). Solo rellena
  // la selección — el usuario puede cambiarla y Clasificar sigue siendo suyo.
  const applySuggestion = (s) => {
    const cat = categories.find((c) => c.key === s.category_key);
    if (cat) {
      setSelectedAccount({ code: cat.account_code, name: cat.label });
      setSelectedCategory(cat.key);
      return;
    }
    const acc = accounts.find((a) => a.code === s.account_code);
    if (acc) {
      setSelectedAccount(acc);
      setSelectedCategory(null);
    }
  };

  const handleClassify = async () => {
    if (!selectedAccount) {
      toast.error("Selecciona una categoría o una cuenta");
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiFetch(`${API}/reconciliation/classify`, {
        method: "POST",
        body: JSON.stringify({
          transaction_id: transaction.transaction_id,
          account_code: selectedAccount.code,
          category: selectedCategory || undefined,
          description: description || undefined,
        }),
      });
      if (response.ok) {
        toast.success("Movimiento clasificado");
        onConfirm();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Error al clasificar");
      }
    } catch {
      toast.error("Error al clasificar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-700">
          Clasificar movimiento
        </span>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* 3.4: sugerencia de categoría (regla manual > IA > nada), en
              componente propio para no tocar la estructura del panel. */}
          <ClassifySuggestion
            transaction={transaction}
            categories={categories}
            accounts={accounts}
            hasSelection={!!selectedAccount}
            onApply={applySuggestion}
          />
          {/* A4: categorías en lenguaje llano primero; el PGC completo queda
              a un clic para usuarios avanzados. */}
          {!showFullChart ? (
            <>
              <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg">
                {visibleCategories.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-3">
                    Sin categorías — usa el plan contable completo
                  </p>
                ) : (
                  visibleCategories.map((cat) => (
                    <button
                      key={cat.key}
                      type="button"
                      className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-2 transition-colors ${
                        selectedCategory === cat.key
                          ? "bg-blue-50"
                          : ""
                      }`}
                      onClick={() => {
                        setSelectedAccount({ code: cat.account_code, name: cat.label });
                        setSelectedCategory(cat.key);
                      }}
                      data-testid={`classify-category-${cat.key}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${selectedCategory === cat.key ? "text-blue-900" : "text-slate-800"}`}>
                          {cat.label}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{cat.description}</p>
                      </div>
                      {selectedCategory === cat.key && (
                        <Check className="w-4 h-4 text-blue-600 shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
              <button
                type="button"
                className="text-xs text-blue-600 underline decoration-dotted hover:text-blue-800"
                onClick={() => setShowFullChart(true)}
                data-testid="show-full-chart-btn"
              >
                Ver plan contable completo (avanzado)
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="text-xs text-blue-600 underline decoration-dotted hover:text-blue-800"
                onClick={() => setShowFullChart(false)}
                data-testid="back-to-categories-btn"
              >
                ← Volver a las categorías
              </button>

              {/* Account search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar cuenta (codigo o nombre)..."
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  className="pl-10"
                  data-testid="account-search-input"
                />
              </div>

              {/* Account list */}
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                {filteredAccounts.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-3">
                    Sin cuentas
                  </p>
                ) : (
                  filteredAccounts.map((acc) => (
                    <button
                      key={acc.account_id || acc.code}
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-2 transition-colors ${
                        selectedAccount?.code === acc.code
                          ? "bg-blue-50 text-blue-900"
                          : "text-slate-700"
                      }`}
                      onClick={() => {
                        setSelectedAccount(acc);
                        setSelectedCategory(null);
                      }}
                      data-testid={`account-option-${acc.code}`}
                    >
                      <span className="font-mono text-xs font-medium text-blue-600 w-20 shrink-0">
                        {acc.code}
                      </span>
                      <span className="truncate flex-1">{acc.name}</span>
                      {acc.usage_count > 0 && (
                        <span
                          className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0"
                          title={`Usada ${acc.usage_count} vez/veces`}
                        >
                          ×{acc.usage_count}
                        </span>
                      )}
                      {selectedAccount?.code === acc.code && (
                        <Check className="w-4 h-4 text-blue-600 shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </>
          )}

          {/* Selected account display */}
          {selectedAccount && (
            <div className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1">
              Seleccionada: {selectedCategory
                ? `${selectedAccount.name} (cuenta ${selectedAccount.code})`
                : `${selectedAccount.code} - ${selectedAccount.name}`}
            </div>
          )}

          {/* Optional description */}
          <Input
            placeholder="Descripcion (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="classify-description-input"
          />

          <Button
            onClick={handleClassify}
            disabled={!selectedAccount || submitting}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="classify-btn"
          >
            {submitting ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Tag className="w-4 h-4 mr-2" />
            )}
            Clasificar
          </Button>
        </>
      )}
    </div>
  );
}

// ── Transaction row with expandable actions ──────────────────────────

function TransactionRow({ transaction, onReconciled, isSelected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(null); // 'suggestions' | 'search' | 'classify' | null

  const togglePanel = (panel) => {
    setExpanded((prev) => (prev === panel ? null : panel));
  };

  const handleReconciled = () => {
    setExpanded(null);
    onReconciled();
  };

  return (
    <div
      className={`border rounded-lg overflow-hidden transition-colors ${
        isSelected ? "border-blue-400 bg-blue-50/30" : "border-slate-200"
      }`}
      data-testid={`tx-row-${transaction.transaction_id}`}
    >
      {/* Main row — en móvil (<sm) se parte en tres líneas: [☑ fecha importe] / descripción / acciones */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 bg-white">
        <div className="shrink-0">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect?.(transaction.transaction_id)}
            data-testid={`tx-select-${transaction.transaction_id}`}
            aria-label="Seleccionar movimiento"
          />
        </div>
        <div className="w-24 shrink-0">
          <span className="font-mono text-sm text-slate-600">
            {formatDate(transaction.date)}
          </span>
        </div>
        <div className="order-last w-full sm:order-none sm:w-auto sm:flex-1 min-w-0">
          <p className="text-sm text-slate-900 truncate">
            {transaction.description}
          </p>
        </div>
        <div className="ml-auto sm:ml-0 w-28 text-right shrink-0">
          <AmountCell amount={transaction.amount} />
        </div>
        <div className="order-last w-full sm:order-none sm:w-auto flex flex-wrap gap-1 shrink-0">
          <Button
            size="sm"
            variant={expanded === "suggestions" ? "default" : "outline"}
            onClick={() => togglePanel("suggestions")}
            className="text-xs"
            title="Ver sugerencias"
            data-testid={`btn-suggestions-${transaction.transaction_id}`}
          >
            <Lightbulb className="w-3.5 h-3.5 mr-1" />
            Sugerencias
          </Button>
          <Button
            size="sm"
            variant={expanded === "search" ? "default" : "outline"}
            onClick={() => togglePanel("search")}
            className="text-xs"
            title="Buscar factura"
            data-testid={`btn-search-${transaction.transaction_id}`}
          >
            <FileSearch className="w-3.5 h-3.5 mr-1" />
            Buscar
          </Button>
          <Button
            size="sm"
            variant={expanded === "classify" ? "default" : "outline"}
            onClick={() => togglePanel("classify")}
            className="text-xs"
            title="Clasificar"
            data-testid={`btn-classify-${transaction.transaction_id}`}
          >
            <Tag className="w-3.5 h-3.5 mr-1" />
            Clasificar
          </Button>
        </div>
      </div>

      {/* Expanded panel */}
      {expanded === "suggestions" && (
        <div className="border-t border-slate-200 bg-slate-50">
          <SuggestionsPanel
            transaction={transaction}
            onConfirm={handleReconciled}
            onClose={() => setExpanded(null)}
          />
        </div>
      )}
      {expanded === "search" && (
        <div className="border-t border-slate-200 bg-slate-50">
          <InvoiceSearchPanel
            transaction={transaction}
            onConfirm={handleReconciled}
            onClose={() => setExpanded(null)}
          />
        </div>
      )}
      {expanded === "classify" && (
        <div className="border-t border-slate-200 bg-slate-50">
          <ClassifyPanel
            transaction={transaction}
            onConfirm={handleReconciled}
            onClose={() => setExpanded(null)}
          />
        </div>
      )}
    </div>
  );
}

// ── Bulk classify toolbar (sticky when >=1 selected) ─────────────────

function BulkClassifyBar({ selectedIds, selectedTransactions, onClear, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  // Misma experiencia que el panel individual (hallazgo smoke 2026-07-11):
  // categorías llanas primero (PGC completo a un clic) y sugerencia si es unánime.
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showFullChart, setShowFullChart] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const appliedRef = useRef(false);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsRes, categoriesRes] = await Promise.all([
          apiFetch(`${API}/accounting/chart?order=usage`),
          apiFetch(`${API}/reconciliation/classify-categories`),
        ]);
        if (accountsRes.ok) {
          const data = await accountsRes.json();
          setAccounts(data.accounts || []);
        }
        if (categoriesRes.ok) {
          const data = await categoriesRes.json();
          setCategories(data.categories || []);
        }
      } catch { /* silencio: la toolbar puede recargarse */ }
    };
    fetchData();
  }, []);

  // Sugerencia del lote (regla manual > IA > nada), solo si es UNÁNIME: todos
  // los seleccionados tienen sugerencia y todas apuntan a lo mismo (el bulk
  // asigna una única cuenta a todos). Si difieren o falta alguna, no se
  // sugiere nada. El endpoint cachea en el movimiento: no se paga dos veces.
  const idsKey = selectedIds.join(",");
  useEffect(() => {
    let cancelled = false;
    setSuggestion(null);
    appliedRef.current = false;
    const fetchSuggestions = async () => {
      try {
        const res = await apiFetch(`${API}/reconciliation/classify-suggestions`, {
          method: "POST",
          body: JSON.stringify({ transaction_ids: idsKey.split(",") }),
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const all = idsKey.split(",").map((id) => data.suggestions?.[id]);
        if (all.some((s) => !s)) return;
        const keyOf = (s) => s.category_key || s.account_code;
        if (all.every((s) => keyOf(s) === keyOf(all[0])) && !cancelled) {
          setSuggestion(all[0]);
        }
      } catch {
        // Sin sugerencia no pasa nada: la clasificación manual sigue intacta.
      }
    };
    fetchSuggestions();
    return () => { cancelled = true; };
  }, [idsKey]);

  // Preselecciona la sugerencia si el usuario aún no eligió nada; espera a que
  // categorías/cuentas estén cargadas antes de darla por aplicada.
  useEffect(() => {
    if (!suggestion || appliedRef.current || selectedAccount) return;
    const cat = categories.find((c) => c.key === suggestion.category_key);
    const acc = !cat && accounts.find((a) => a.code === suggestion.account_code);
    if (!cat && !acc) return;
    appliedRef.current = true;
    if (cat) {
      setSelectedAccount({ code: cat.account_code, name: cat.label });
      setSelectedCategory(cat.key);
    } else {
      setSelectedAccount(acc);
      setSelectedCategory(null);
    }
  }, [suggestion, categories, accounts, selectedAccount]);

  const filteredAccounts = accounts.filter((acc) => {
    if (!accountSearch) return true;
    const q = accountSearch.toLowerCase();
    return acc.code.includes(q) || acc.name.toLowerCase().includes(q);
  });

  // Categorías llanas solo si todos los seleccionados comparten signo (una
  // misma categoría para cargos y abonos a la vez no tiene sentido).
  const kinds = new Set(
    (selectedTransactions || []).map((t) => (t.amount < 0 ? "gasto" : "ingreso"))
  );
  const singleKind = kinds.size === 1 ? [...kinds][0] : null;
  const visibleCategories = singleKind
    ? categories.filter((c) => c.kind === singleKind)
    : [];
  const showCategories = !showFullChart && visibleCategories.length > 0;

  const suggestedCat = suggestion && categories.find((c) => c.key === suggestion.category_key);
  const suggestedAcc = suggestion && !suggestedCat && accounts.find((a) => a.code === suggestion.account_code);
  const suggestionLabel = suggestion
    ? (suggestedCat?.label || (suggestedAcc ? `${suggestedAcc.code} - ${suggestedAcc.name}` : suggestion.account_code))
    : null;

  const handleBulkClassify = async () => {
    if (!selectedAccount || selectedIds.length === 0) return;
    setSubmitting(true);
    // Ejecutamos en SERIE (no en paralelo) para evitar WriteConflicts en MongoDB:
    // el endpoint /classify reserva un entry_number leyendo el último + 1, y N llamadas
    // simultáneas chocan al insertar el mismo número en accounting_entries.
    let ok = 0;
    const fails = [];
    try {
      for (const txId of selectedIds) {
        try {
          const r = await apiFetch(`${API}/reconciliation/classify`, {
            method: "POST",
            body: JSON.stringify({
              transaction_id: txId,
              account_code: selectedAccount.code,
              category: selectedCategory || undefined,
              description: description || undefined,
            }),
          });
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            fails.push({ txId, msg: err.detail || `HTTP ${r.status}` });
          } else {
            ok += 1;
          }
        } catch (e) {
          fails.push({ txId, msg: e?.message || "error" });
        }
      }
      if (ok > 0 && fails.length === 0) {
        toast.success(`${ok} movimientos clasificados con ${selectedAccount.code}`);
      } else if (ok > 0 && fails.length > 0) {
        console.warn("[BulkClassify] fallidos:", fails);
        toast.warning(`${ok} clasificados, ${fails.length} fallaron. Revisa la consola para detalle.`);
      } else {
        console.warn("[BulkClassify] fallidos:", fails);
        toast.error(`Ningún movimiento clasificado: ${fails[0]?.msg || "error"}`);
      }
      onDone();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="sticky top-2 z-20 border-blue-300 shadow-md">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedIds.length} movimiento{selectedIds.length !== 1 ? "s" : ""} seleccionado{selectedIds.length !== 1 ? "s" : ""}
          </span>
          <Button variant="ghost" size="sm" onClick={onClear} disabled={submitting}>
            <X className="w-4 h-4 mr-1" />
            Cancelar selección
          </Button>
        </div>

        {suggestion && (
          <div
            className="flex flex-wrap items-center gap-1.5 text-xs text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1.5 min-w-0"
            data-testid="bulk-classify-suggestion"
          >
            {suggestion.source === "ai" ? (
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
            ) : (
              <Workflow className="w-3.5 h-3.5 shrink-0" />
            )}
            <span className="font-medium shrink-0">
              {suggestion.source === "ai" ? "Sugerencia IA" : "Regla aplicada"} para los {selectedIds.length} seleccionados:
            </span>
            <span className="truncate min-w-0" title={suggestion.reason}>
              {suggestionLabel}
              {suggestion.source === "ai" && suggestion.confidence != null
                ? ` (confianza ${confidenceLabel(suggestion.confidence)})`
                : ""}
            </span>
          </div>
        )}

        <div className="grid grid-cols-12 gap-2 items-start">
          <div className="col-span-7">
            {showCategories ? (
              <>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded">
                  {visibleCategories.map((cat) => (
                    <button
                      key={cat.key}
                      type="button"
                      className={`w-full text-left px-3 py-1.5 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-2 ${
                        selectedCategory === cat.key ? "bg-blue-50" : ""
                      }`}
                      onClick={() => {
                        setSelectedAccount({ code: cat.account_code, name: cat.label });
                        setSelectedCategory(cat.key);
                      }}
                      data-testid={`bulk-category-${cat.key}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${selectedCategory === cat.key ? "text-blue-900" : "text-slate-800"}`}>
                          {cat.label}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{cat.description}</p>
                      </div>
                      {selectedCategory === cat.key && (
                        <Check className="w-4 h-4 text-blue-600 shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-1 text-xs text-blue-600 underline decoration-dotted hover:text-blue-800"
                  onClick={() => setShowFullChart(true)}
                  data-testid="bulk-show-full-chart-btn"
                >
                  Ver plan contable completo (avanzado)
                </button>
              </>
            ) : (
              <>
                {visibleCategories.length > 0 && (
                  <button
                    type="button"
                    className="mb-1 text-xs text-blue-600 underline decoration-dotted hover:text-blue-800"
                    onClick={() => setShowFullChart(false)}
                    data-testid="bulk-back-to-categories-btn"
                  >
                    ← Volver a las categorías
                  </button>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar cuenta (código o nombre)..."
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    className="pl-10"
                    data-testid="bulk-account-search"
                  />
                </div>
                <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded">
                  {filteredAccounts.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-2">Sin cuentas</p>
                  ) : (
                    filteredAccounts.slice(0, 100).map((acc) => (
                      <button
                        key={acc.account_id || acc.code}
                        type="button"
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-2 ${
                          selectedAccount?.code === acc.code ? "bg-blue-50 text-blue-900" : "text-slate-700"
                        }`}
                        onClick={() => { setSelectedAccount(acc); setSelectedCategory(null); }}
                        data-testid={`bulk-account-${acc.code}`}
                      >
                        <span className="font-mono text-xs font-medium text-blue-600 w-16 shrink-0">{acc.code}</span>
                        <span className="truncate flex-1">{acc.name}</span>
                        {acc.usage_count > 0 && (
                          <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">
                            ×{acc.usage_count}
                          </span>
                        )}
                        {selectedAccount?.code === acc.code && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          <div className="col-span-5 space-y-2">
            {selectedAccount && (
              <div className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1">
                Seleccionada: <span className="font-medium">{selectedAccount.code}</span> — {selectedAccount.name}
              </div>
            )}
            <Input
              placeholder="Descripción (opcional, se aplica a todos)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="bulk-classify-description"
            />
            <Button
              onClick={handleBulkClassify}
              disabled={!selectedAccount || submitting}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="bulk-classify-btn"
            >
              {submitting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Tag className="w-4 h-4 mr-2" />
              )}
              Clasificar {selectedIds.length} con esta cuenta
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Pending transactions section ─────────────────────────────────────

function PendingSection() {
  const [transactions, setTransactions] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("asc"); // asc = más antiguas primero (cronológico)
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggleSelect = useCallback((txId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(txId)) next.delete(txId);
      else next.add(txId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch pending transactions con limit alto y filtros opcionales
      const params = new URLSearchParams({ status: "pending", limit: "1000" });
      if (searchText.trim()) params.set("search", searchText.trim());
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const pendingRes = await apiFetch(`${API}/bank/transactions?${params}`);
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setTransactions(data.transactions || []);
        setTotalPending(data.total || (data.transactions || []).length);
      }
      // Fetch all to compute progress (sin filtros, total real)
      const allRes = await apiFetch(`${API}/bank/transactions?limit=1`);
      if (allRes.ok) {
        const data = await allRes.json();
        setTotalAll(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching pending transactions:", error);
      toast.error("Error al cargar movimientos pendientes");
    } finally {
      setLoading(false);
    }
  }, [searchText, startDate, endDate]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // Reordenar localmente (el backend siempre devuelve DESC, invertimos si pidieron ASC)
  const sortedTransactions = [...transactions].sort((a, b) => {
    const da = a.date || "";
    const db = b.date || "";
    if (sortOrder === "asc") return da.localeCompare(db);
    return db.localeCompare(da);
  });

  const reconciled = totalAll - totalPending;
  const progressPercent = totalAll > 0 ? (reconciled / totalAll) * 100 : 0;
  const hasFilters = searchText.trim() || startDate || endDate;

  return (
    <div className="space-y-4">
      {/* Progress indicator */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              {reconciled} de {totalAll} movimientos conciliados
            </span>
            <span className="text-sm font-medium text-slate-500">
              {Math.round(progressPercent)}%
            </span>
          </div>
          <Progress value={progressPercent} className="h-2.5 bg-slate-200" />
        </CardContent>
      </Card>

      {/* Filtros y orden */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs text-slate-600 mb-1">Buscar en concepto</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Proveedor, concepto..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Desde</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Hasta</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <Button
              variant="outline"
              onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
              title={sortOrder === "asc" ? "Más antiguas primero" : "Más recientes primero"}
            >
              <ArrowUpDown className="w-4 h-4 mr-2" />
              {sortOrder === "asc" ? "Antiguas → recientes" : "Recientes → antiguas"}
            </Button>
            {hasFilters && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSearchText("");
                  setStartDate("");
                  setEndDate("");
                }}
              >
                Limpiar
              </Button>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Mostrando {sortedTransactions.length} de {totalPending} pendientes
            {sortedTransactions.length >= 1000 && " (límite 1000 — usa filtros para acotar)"}
          </div>
        </CardContent>
      </Card>

      {/* Transaction list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner" />
        </div>
      ) : sortedTransactions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Check className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {hasFilters ? "Sin resultados" : "Todo conciliado"}
            </h3>
            <p className="text-sm text-slate-500">
              {hasFilters
                ? "Ajusta los filtros para ver más movimientos"
                : "No hay movimientos pendientes de conciliar"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {selectedIds.size > 0 && (
            <BulkClassifyBar
              selectedIds={Array.from(selectedIds)}
              selectedTransactions={sortedTransactions.filter((t) => selectedIds.has(t.transaction_id))}
              onClear={clearSelection}
              onDone={() => {
                clearSelection();
                fetchPending();
              }}
            />
          )}
          {sortedTransactions.map((tx) => (
            <TransactionRow
              key={tx.transaction_id}
              transaction={tx}
              onReconciled={fetchPending}
              isSelected={selectedIds.has(tx.transaction_id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── History section ──────────────────────────────────────────────────

function HistorySection() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [undoTarget, setUndoTarget] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const period = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const response = await apiFetch(
        `${API}/reconciliation/history?period=${period}`
      );
      if (response.ok) {
        const data = await response.json();
        setRecords(data.records || data.reconciliations || []);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Error al cargar historial");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleUndo = async () => {
    if (!undoTarget) return;
    setUndoing(true);
    try {
      const response = await apiFetch(
        `${API}/reconciliation/${undoTarget.reconciliation_id}`,
        { method: "DELETE" }
      );
      if (response.ok) {
        toast.success("Conciliacion deshecha");
        setRecords((prev) =>
          prev.filter(
            (r) => r.reconciliation_id !== undoTarget.reconciliation_id
          )
        );
      } else {
        const err = await response.json();
        toast.error(err.detail || "Error al deshacer");
      }
    } catch {
      toast.error("Error al deshacer");
    } finally {
      setUndoing(false);
      setUndoTarget(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <History className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            Sin historial
          </h3>
          <p className="text-sm text-slate-500">
            Las conciliaciones confirmadas apareceran aqui
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="table-dense min-w-[750px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Fecha</TableHead>
                  <TableHead>Movimiento</TableHead>
                  <TableHead>Factura / Cuenta</TableHead>
                  <TableHead className="text-right w-[120px]">
                    Importe
                  </TableHead>
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((rec) => (
                  <TableRow
                    key={rec.reconciliation_id}
                    data-testid={`history-row-${rec.reconciliation_id}`}
                  >
                    <TableCell className="font-mono text-sm">
                      {formatDate(rec.date || rec.created_at)}
                    </TableCell>
                    <TableCell className="max-w-[250px] truncate">
                      {rec.transaction_description || rec.description || "-"}
                    </TableCell>
                    <TableCell>
                      {rec.invoice_number
                        ? `Factura #${rec.invoice_number}`
                        : rec.account_code
                        ? `${rec.account_code} - ${rec.account_name || ""}`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <AmountCell amount={rec.amount || 0} />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setUndoTarget(rec)}
                        className="text-slate-500 hover:text-rose-600"
                        data-testid={`undo-${rec.reconciliation_id}`}
                      >
                        <Undo2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Confirm undo dialog */}
      <AlertDialog
        open={!!undoTarget}
        onOpenChange={(open) => !open && setUndoTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deshacer conciliacion</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminara la conciliacion y sus asientos contables (incluida
              la diferencia de cambio si la hubo), revirtiendo los saldos. El
              movimiento bancario volvera a pendiente y el estado de cobro/pago
              de la factura se recalculara con los cobros restantes; en divisa,
              el EUR fijado por el banco se revierte. Podras volver a conciliar
              cuando quieras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undoing}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUndo}
              disabled={undoing}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {undoing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Undo2 className="w-4 h-4 mr-2" />
              )}
              Deshacer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Main page ────────────────────────────────────────────────────────

export default function Reconciliation() {
  return (
    <div className="space-y-6" data-testid="reconciliation-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Conciliacion Bancaria
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Vincula movimientos bancarios con facturas o clasifica manualmente
        </p>
      </div>

      {/* Info card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <ArrowLeftRight className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">
                Conciliacion inteligente
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Usa <strong>Sugerencias</strong> para ver emparejamientos
                automaticos, <strong>Buscar</strong> para vincular
                manualmente con una factura, o <strong>Clasificar</strong>{" "}
                para asignar una cuenta contable directamente.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger
            value="pending"
            data-testid="tab-pending"
            className="flex items-center gap-1.5"
          >
            <Inbox className="w-4 h-4" />
            Pendientes
          </TabsTrigger>
          <TabsTrigger
            value="history"
            data-testid="tab-history"
            className="flex items-center gap-1.5"
          >
            <History className="w-4 h-4" />
            Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <PendingSection />
        </TabsContent>

        <TabsContent value="history">
          <HistorySection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
